import React, { createContext, useContext, useState, useEffect } from 'react';
import { getFiberSiteDashboard } from '../functions/get_fiber_site_dashboard.function';

const FiberDashboardContext = createContext();

export const useFiberDashboard = () => {
  const context = useContext(FiberDashboardContext);
  if (!context) {
    throw new Error('useFiberDashboard must be used within a FiberDashboardProvider');
  }
  return context;
};

export const FiberDashboardProvider = ({ children }) => {
  const [dashboardData, setDashboardData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [paginationUpdateCallback, setPaginationUpdateCallback] = useState(null);

  // Cache duration: 5 minutes (300000 ms)
  const CACHE_DURATION = 5 * 60 * 1000;

  const fetchDashboardData = async (forceRefresh = false) => {
    // Check if we have cached data and it's still valid
    if (!forceRefresh && dashboardData.length > 0 && lastFetchTime) {
      const timeSinceLastFetch = Date.now() - lastFetchTime;
      if (timeSinceLastFetch < CACHE_DURATION) {
        console.log('Using cached dashboard data');
        return dashboardData;
      }
    }

    // Don't fetch if already loading
    if (loading) {
      console.log('Dashboard data already loading, skipping...');
      return dashboardData;
    }

    try {
      setLoading(true);
      setError(null);
      console.log('Fetching fresh dashboard data...');

      const response = await getFiberSiteDashboard();
      const data = Array.isArray(response) ? response : [];

      setDashboardData(data);
      setLastFetchTime(Date.now());
      setIsInitialized(true);

      console.log('Dashboard data fetched successfully:', data.length, 'sites');

      // Call pagination update callback if provided
      if (paginationUpdateCallback && typeof paginationUpdateCallback === 'function') {
        paginationUpdateCallback(data.length);
      }

      return data;
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError(error);
      return dashboardData; // Return cached data if available
    } finally {
      setLoading(false);
    }
  };

  const refreshDashboardData = () => {
    return fetchDashboardData(true);
  };

  const clearCache = () => {
    setDashboardData([]);
    setLastFetchTime(null);
    setIsInitialized(false);
  };

  const value = {
    dashboardData,
    loading,
    error,
    isInitialized,
    fetchDashboardData,
    refreshDashboardData,
    clearCache,
    lastFetchTime,
    setPaginationUpdateCallback,
  };

  return <FiberDashboardContext.Provider value={value}>{children}</FiberDashboardContext.Provider>;
};
